import React, { useEffect, useRef } from 'react';
import SectionLabel from '../ui/SectionLabel';
import './Process.css';

const steps = [
  {
    id: 's1',
    num: '01',
    title: 'Discovery',
    desc: 'Audit your data landscape, define measurable KPIs, and scope a lean MVP that de-risks early.',
  },
  {
    id: 's2',
    num: '02',
    title: 'Design',
    desc: 'Select the right architecture, model stack, and infra — then validate with a working proof of concept.',
  },
  {
    id: 's3',
    num: '03',
    title: 'Build',
    desc: 'Sprint-based delivery with fortnightly demos. You see progress every two weeks, not just at the end.',
  },
  {
    id: 's4',
    num: '04',
    title: 'Deploy',
    desc: 'CI/CD pipelines, production monitoring, and cutover planning — so go-live is smooth, not a gamble.',
  },
  {
    id: 's5',
    num: '05',
    title: 'Scale',
    desc: 'Automated retraining, drift detection, and a living roadmap so your AI keeps improving over time.',
  },
];

const Process: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const targets = sectionRef.current?.querySelectorAll<HTMLElement>('.reveal');
    if (!targets) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    targets.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <section className="process" id="process" ref={sectionRef}>
      <div className="process__inner">

        <div className="process__head reveal">
          <SectionLabel text="How We Work" />
          <h2 className="process__title">
            Our <em>Delivery</em><br />
            Framework
          </h2>
          <p className="process__sub">
            Five phases, zero surprises. Every engagement follows this
            battle-tested process — from first call to production cutover.
          </p>
        </div>

        <div className="process__steps">
          {steps.map((s, i) => (
            <div
              className={`process__step reveal reveal-delay-${Math.min(i, 4)}`}
              key={s.id}
            >
              <div className="process__dot">{s.num}</div>
              <div className="process__step-title">{s.title}</div>
              <p className="process__step-desc">{s.desc}</p>
              <div className="process__step-ghost">{s.num}</div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Process;